/**
 * ApproverFlowEditor - 審批流程編輯器（重構版）
 * 
 * 基於第一性原則設計：
 * 1. 數據驅動：approvalFlow 是唯一的數據來源
 * 2. 權限控制：flowSettings + stepEditing 決定操作權限
 * 3. 可組合性：使用獨立子組件
 * 4. 浮動視窗：用戶選擇器使用 Modal
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import StepCard from './StepCard';
import UserMultiSelector from './UserMultiSelector';
import { useI18n } from '../../context/I18nContext';

/**
 * ApproverFlowEditor 主組件
 * 
 * @param {Object} props
 * @param {Array} props.approvalFlow - 審批流程數據
 * @param {Function} props.onChange - 流程變更回調
 * @param {Object} props.flowSettings - 流程設定（來自 route.json）
 * @param {boolean} props.isAdmin - 是否為管理員
 * @param {string} props.formStatus - 表單狀態
 * @param {string} props.currentUserId - 當前用戶 ID
 * @param {Function} props.getText - 多語言文字解析函數
 * @param {boolean} props.readOnly - 是否只讀
 */
export default function ApproverFlowEditor({
    approvalFlow = [],
    onChange,
    flowSettings = {},
    isAdmin = false,
    formStatus = 'draft',
    currentUserId,
    getText,
    readOnly = false,
}) {
    const { t } = useI18n(['common', 'forms']);
    
    // 展開狀態管理
    const [expandedSteps, setExpandedSteps] = useState(new Set());
    
    // 用戶選擇器狀態
    const [selectorState, setSelectorState] = useState({
        isOpen: false,
        mode: null, // 'edit' | 'add' | 'insert'
        targetStep: null,
        targetApprover: null,
        insertPosition: null, // 'before' | 'after'
        insertIndex: null,
    });

    // 是否可編輯
    const canEdit = useMemo(() => {
        if (readOnly) return false;
        if (isAdmin && flowSettings.adminCanOverride !== false) return true;
        return formStatus === 'draft' || formStatus === 'returned';
    }, [readOnly, isAdmin, flowSettings, formStatus]);

    // 是否可拖拉
    const canReorder = useMemo(() => {
        return canEdit && (flowSettings.allowReorderStep ?? true);
    }, [canEdit, flowSettings]);

    // DnD 設定
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // 處理拖拉結束
    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = approvalFlow.findIndex(s => s.stepId === active.id);
        const newIndex = approvalFlow.findIndex(s => s.stepId === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            // 不允許移動 submit 和 completed 步驟
            const movingStep = approvalFlow[oldIndex];
            const targetStep = approvalFlow[newIndex];
            if (movingStep.stepType === 'submit' || 
                movingStep.stepType === 'completed' ||
                targetStep.stepType === 'submit' ||
                targetStep.stepType === 'completed') {
                return;
            }

            const newFlow = arrayMove(approvalFlow, oldIndex, newIndex).map((step, idx) => ({
                ...step,
                stepIndex: idx + 1,
                order: idx + 1,
            }));
            onChange?.(newFlow);
        }
    }, [approvalFlow, onChange]);

    // 切換步驟展開
    const handleToggleExpand = useCallback((stepId) => {
        setExpandedSteps(prev => {
            const next = new Set(prev);
            if (next.has(stepId)) {
                next.delete(stepId);
            } else {
                next.add(stepId);
            }
            return next;
        });
    }, []);

    // 開啟編輯審批人
    const handleEditApprovers = useCallback((step, approver = null, newMode = null) => {
        // 如果是更改審批模式
        if (newMode !== null) {
            const newFlow = approvalFlow.map(s => {
                if (s.stepId === step.stepId) {
                    return { ...s, approvalMode: newMode, isOverridden: true };
                }
                return s;
            });
            onChange?.(newFlow);
            return;
        }

        // 開啟用戶選擇器
        setSelectorState({
            isOpen: true,
            mode: 'edit',
            targetStep: step,
            targetApprover: approver,
            insertPosition: null,
            insertIndex: null,
        });
    }, [approvalFlow, onChange]);

    // 開啟新增審批人到步驟
    const handleAddApproverToStep = useCallback((step) => {
        setSelectorState({
            isOpen: true,
            mode: 'add',
            targetStep: step,
            targetApprover: null,
            insertPosition: null,
            insertIndex: null,
        });
    }, []);

    // 移除審批人
    const handleRemoveApprover = useCallback((step, approver) => {
        const newFlow = approvalFlow.map(s => {
            if (s.stepId === step.stepId) {
                const newApprovers = s.approvers.filter(a => 
                    a.userId !== approver.userId && a.email !== approver.email
                );
                // 至少保留一個審批人
                if (newApprovers.length === 0) return s;
                return { ...s, approvers: newApprovers, isOverridden: true };
            }
            return s;
        });
        onChange?.(newFlow);
    }, [approvalFlow, onChange]);

    // 重新排序審批人（依序審批模式下）
    const handleReorderApprovers = useCallback((step, newApprovers) => {
        const newFlow = approvalFlow.map(s => {
            if (s.stepId === step.stepId) {
                return { ...s, approvers: newApprovers, isOverridden: true };
            }
            return s;
        });
        onChange?.(newFlow);
    }, [approvalFlow, onChange]);

    // 開啟插入步驟
    const handleInsertStep = useCallback((step, index, position) => {
        setSelectorState({
            isOpen: true,
            mode: 'insert',
            targetStep: step,
            targetApprover: null,
            insertPosition: position,
            insertIndex: position === 'before' ? index : index + 1,
        });
    }, []);

    // 確認用戶選擇
    const handleSelectorConfirm = useCallback(({ users, approvalMode }) => {
        const { mode, targetStep, targetApprover, insertIndex } = selectorState;

        if (mode === 'edit') {
            // 替換整個步驟的審批人（編輯時帶入該 stage 全部人員）
            const newFlow = approvalFlow.map(s => {
                if (s.stepId === targetStep.stepId) {
                    return { 
                        ...s, 
                        approvers: users.map(u => ({ ...u, role: s.approverType })),
                        approvalMode: approvalMode,
                        isOverridden: true,
                    };
                }
                return s;
            });
            onChange?.(newFlow);
        } else if (mode === 'add') {
            // 新增審批人到步驟
            const newFlow = approvalFlow.map(s => {
                if (s.stepId === targetStep.stepId) {
                    // 取得現有審批人的 ID 和 email
                    const existingUserIds = new Set(s.approvers.map(a => a.userId).filter(Boolean));
                    const existingEmails = new Set(s.approvers.map(a => a.email).filter(Boolean));
                    
                    // 過濾掉已存在的用戶
                    const newApprovers = users.filter(u => 
                        !existingUserIds.has(u.userId) && !existingEmails.has(u.email)
                    );
                    
                    // 如果沒有新用戶要加入，直接返回原步驟
                    if (newApprovers.length === 0) {
                        return s;
                    }
                    
                    // 合併現有和新增的審批人
                    const mergedApprovers = [
                        ...s.approvers,
                        ...newApprovers.map(u => ({ ...u, role: s.approverType }))
                    ];
                    
                    return { 
                        ...s, 
                        approvers: mergedApprovers,
                        approvalMode: mergedApprovers.length > 1 ? (approvalMode || s.approvalMode) : s.approvalMode,
                        isOverridden: true,
                    };
                }
                return s;
            });
            onChange?.(newFlow);
        } else if (mode === 'insert') {
            // 插入新步驟
            const newStep = {
                stepId: `inserted_${Date.now()}`,
                stepIndex: insertIndex + 1,
                order: insertIndex + 1,
                stepName: { 
                    en: 'Additional Approval', 
                    'zh-TW': '追加審批', 
                    'zh-CN': '追加审批' 
                },
                stepType: 'approval',
                approverType: 'added',
                status: 'pending',
                approvers: users.map(u => ({ ...u, role: 'added' })),
                approvalMode: approvalMode,
                isOverridden: false,
                isAdded: true,
                stepEditing: {
                    canModifyApprover: true,
                    canAddApprover: true,
                    canRemove: true,
                    canInsertBefore: true,
                    canInsertAfter: true,
                },
            };

            const newFlow = [
                ...approvalFlow.slice(0, insertIndex),
                newStep,
                ...approvalFlow.slice(insertIndex),
            ].map((step, idx) => ({
                ...step,
                stepIndex: idx + 1,
                order: idx + 1,
            }));
            onChange?.(newFlow);
        }

        // 關閉選擇器
        setSelectorState({
            isOpen: false,
            mode: null,
            targetStep: null,
            targetApprover: null,
            insertPosition: null,
            insertIndex: null,
        });
    }, [selectorState, approvalFlow, onChange]);

    // 關閉選擇器
    const handleSelectorCancel = useCallback(() => {
        setSelectorState({
            isOpen: false,
            mode: null,
            targetStep: null,
            targetApprover: null,
            insertPosition: null,
            insertIndex: null,
        });
    }, []);

    // 刪除已添加的步驟
    const handleRemoveStep = useCallback((stepId) => {
        const step = approvalFlow.find(s => s.stepId === stepId);
        if (!step?.isAdded) return; // 只能刪除新增的步驟

        const newFlow = approvalFlow
            .filter(s => s.stepId !== stepId)
            .map((step, idx) => ({
                ...step,
                stepIndex: idx + 1,
                order: idx + 1,
            }));
        onChange?.(newFlow);
    }, [approvalFlow, onChange]);

    // 獲取選擇器標題
    const getSelectorTitle = () => {
        const { mode, targetStep, targetApprover } = selectorState;
        const stepName = targetStep ? (getText ? getText(targetStep.stepName) : targetStep.stepName) : '';
        
        if (mode === 'edit' && targetApprover) {
            return `${t('forms:route.replaceApprover') || '替換審批人'}: ${targetApprover.displayName}`;
        }
        if (mode === 'edit') {
            return `${t('forms:route.editApprovers') || '編輯審批人'}: ${stepName}`;
        }
        if (mode === 'add') {
            return `${t('forms:route.addApproverTo') || '新增審批人到'}: ${stepName}`;
        }
        if (mode === 'insert') {
            return t('forms:route.insertStep') || '插入審批步驟';
        }
        return t('forms:route.selectApprovers') || '選擇審批人';
    };

    // 可排序的步驟 ID
    const sortableIds = useMemo(() => {
        return approvalFlow
            .filter(s => s.stepType === 'approval')
            .map(s => s.stepId);
    }, [approvalFlow]);

    return (
        <div className="bg-white rounded-lg shadow">
            {/* 標題 */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-medium text-gray-900">
                    {t('forms:route.title') || '審批流程'}
                </h3>
                {canEdit && (flowSettings.allowInsertStep ?? true) && (
                    <button
                        onClick={() => {
                            // 找到最後一個審批步驟的位置
                            const lastApprovalIndex = approvalFlow.findLastIndex(s => s.stepType === 'approval');
                            if (lastApprovalIndex !== -1) {
                                handleInsertStep(approvalFlow[lastApprovalIndex], lastApprovalIndex, 'after');
                            }
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        {t('forms:route.addStep') || '新增步驟'}
                    </button>
                )}
            </div>

            {/* 步驟列表 */}
            <div className="p-4">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={sortableIds}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="space-y-6">
                            {approvalFlow.map((step, idx) => (
                                <StepCard
                                    key={step.stepId}
                                    step={step}
                                    index={idx}
                                    canEdit={canEdit}
                                    canDrag={canReorder && step.stepType === 'approval' && !step.isSkipped}
                                    isExpanded={expandedSteps.has(step.stepId)}
                                    onToggleExpand={handleToggleExpand}
                                    onEditApprovers={handleEditApprovers}
                                    onAddApprover={handleAddApproverToStep}
                                    onRemoveApprover={handleRemoveApprover}
                                    onReorderApprovers={handleReorderApprovers}
                                    onInsertBefore={(s, i) => handleInsertStep(s, i, 'before')}
                                    onInsertAfter={(s, i) => handleInsertStep(s, i, 'after')}
                                    onRemoveStep={handleRemoveStep}
                                    getText={getText}
                                    flowSettings={flowSettings}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            </div>

            {/* 用戶選擇器 Modal */}
            {selectorState.isOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="font-medium text-gray-900">
                                {getSelectorTitle()}
                            </h3>
                            <button
                                onClick={handleSelectorCancel}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-4">
                            <UserMultiSelector
                                selectedUsers={
                                    selectorState.mode === 'insert'
                                        ? []
                                        : selectorState.targetStep?.approvers || []
                                }
                                approvalMode={selectorState.targetStep?.approvalMode || 'sequential'}
                                maxUsers={flowSettings.maxApproversPerStep || 5}
                                minUsers={flowSettings.requireMinApprovers || 1}
                                showApprovalMode={
                                    selectorState.mode === 'insert' || 
                                    (selectorState.mode === 'edit' && !selectorState.targetApprover)
                                }
                                onConfirm={handleSelectorConfirm}
                                onCancel={handleSelectorCancel}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
